import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Avatar, Badge, Box, Button, HStack, Heading, Icon, SimpleGrid, Spinner, Text, VStack, Wrap, WrapItem, chakra, Separator, BoxProps, SimpleGridProps, StackProps, Presence } from '@chakra-ui/react';
import { UserSignupEmailCaptureForm } from '@src/components/sco/Account/UserSignupEmailCaptureForm';
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea';
import { ComboboxItem } from '@src/components/sco/ComboboxTagInput';
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer';
import { UserSearchBuilderSignupModal } from '@src/components/sco/Matching/UserSearchBuilder/UserSearchBuilderSignupModal';
import { UserSearchToggleButton } from '@src/components/sco/Matching/UserSearchBuilder/UserSearchToggleButton';
import { ctaVariants, defaultPrimarySkillTitles, generatePrompt } from '@src/components/sco/Matching/UserSearchBuilder/lib';
import { SelectableButtons } from '@src/components/sco/SelectableButtons';
import { SkillsComboboxInput } from '@src/components/sco/SkillsComboboxInput';
import { useDelayedText } from '@src/components/sco/TypingText';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { SSRGate } from '@src/components/sco/utilities/SSRGate';
import { memberMatchUserRolesSelectableButtonOptions } from '@src/constants/user-roles';
import { RegisterUserPayload, ScoUserSearch, ScoUserSearchDraft, UpsertUserSearchInput } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { TrackEvent } from '@src/lib/TrackEvent';
import { gql } from '@src/lib/gql';
import { hourSeed, seededShuffle } from '@src/lib/seededShuffle';
import tracker from '@src/lib/tracker';
import { useDebounce } from '@uidotdev/usehooks';
import { useRouter } from 'next/navigation';
import { parseAsArrayOf, parseAsBoolean, parseAsString, useQueryState } from 'nuqs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CountUp from 'react-countup';
import { LuArrowLeft, LuArrowRight, LuPencil } from 'react-icons/lu';
import { useInterval } from 'react-use';
type UserSearchBuilderProps = {
  containerBoxProps?: Partial<BoxProps>;
  ctaBoxProps?: Partial<StackProps>;
  defaultFormValues?: {
    userRoleKeys?: string[];
    selectedSkillTitles?: string[];
  };
  emailCaptureMethod?: 'modal' | 'inline';
  hide?: {
    title?: boolean;
    contentBox?: boolean;
    matchCount?: boolean;
    matchedUsers?: boolean;
    selectedSkillTitles?: boolean;
    additionalSkillTitles?: boolean;
    userRoleKeys?: boolean;
    skillSearch?: boolean;
  };
  labels?: {
    userRoleKeys?: any;
    primarySkills?: any;
    additionalSkills?: any;
    title?: string;
  };
  colors?: {
    labels?: BoxProps['color'];
  };
  formPlacement?: 'left' | 'right';
  formBoxProps?: Partial<StackProps>;
  formInnerBoxProps?: Partial<StackProps>;
  primarySkillTitles?: string[];
  renderContent?: () => any;
  simpleGridProps?: SimpleGridProps;
  contentBoxProps?: Partial<StackProps>;
  contentInnerBoxProps?: Partial<StackProps>;
  userSearch?: Partial<ScoUserSearch>;
};
export const UserSearchBuilder = props => {
  const $ = _c(111);
  const router = useRouter();
  const {
    currentUser
  } = useCurrentUser();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const primarySkillTitles = props.primarySkillTitles || defaultPrimarySkillTitles;
  let t0;
  if ($[0] !== getBreakpointValue) {
    t0 = getBreakpointValue([true, false, false]);
    $[0] = getBreakpointValue;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isMobileVariant = t0;
  const isFormOnRight = props.formPlacement === "right";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [showNaturalLanguagePrompt, setShowNaturalLanguagePrompt] = useState(false);
  const [signup] = useQueryState("signup", parseAsBoolean);
  const [requireSignup, setRequireSignup] = useState(signup);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [userSearchDraft, setUserSearchDraft] = useState(t1);
  const debouncedMatchedUsersCount = useDebounce(userSearchDraft.matchedUsersCount, 1500);
  const [ctaVariantKey] = useQueryState("v", parseAsString);
  const ctaVariant = ctaVariants[ctaVariantKey] || ctaVariants.default;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [addedSkillTitles, setAddedSkillTitles] = useState(t2);
  const [naturalLanguagePrompt, setNaturalLanguagePrompt] = useState("");
  let t3;
  if ($[4] !== props.defaultFormValues?.userRoleKeys) {
    t3 = parseAsArrayOf(parseAsString).withDefault(props.defaultFormValues?.userRoleKeys || []);
    $[4] = props.defaultFormValues?.userRoleKeys;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [userRoleKeys, setUserRoleKeys] = useQueryState("roles", t3);
  let t4;
  if ($[6] !== props.defaultFormValues?.selectedSkillTitles) {
    t4 = parseAsArrayOf(parseAsString).withDefault(props.defaultFormValues?.selectedSkillTitles || []);
    $[6] = props.defaultFormValues?.selectedSkillTitles;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [selectedSkillTitles, setSelectedSkillTitles] = useQueryState("skills", t4);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = gql`
    mutation ($input: UpsertUserSearchInput!) {
      upsertUserSearch(input: $input) {
        userSearch {
          id
          userSearchId
        }
      }
    }
  `;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const [upsertUserSearch, upsertUserSearchMutation] = useMutation(t5);
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = gql`
      query ($selectedSkillTitles: [String]) {
        scoUserSearchDraft(selectedSkillTitles: $selectedSkillTitles, matchedUsersLimit: 24) {
          id
          relevantSkills
          matchedUsers {
            id
            fullName
            pictureUrl
          }
          matchedUsersCount
        }
      }
    `;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = !!props.hide?.additionalSkillTitles;
  let t8;
  if ($[10] !== selectedSkillTitles) {
    t8 = {
      selectedSkillTitles
    };
    $[10] = selectedSkillTitles;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = {
      ssr: false,
      skip: t7,
      variables: t8
    };
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const draftQuery = useQuery(t6, t9);
  let t10;
  if ($[15] !== selectedSkillTitles || $[16] !== setSelectedSkillTitles) {
    t10 = skillTitle => {
      if (selectedSkillTitles.includes(skillTitle)) {
        setSelectedSkillTitles(st => st.filter(x => x !== skillTitle));
      } else {
        setSelectedSkillTitles(x_0 => [...x_0, skillTitle]);
      }
    };
    $[15] = selectedSkillTitles;
    $[16] = setSelectedSkillTitles;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const toggleSelectedSkill = t10;
  let t11;
  if ($[18] !== addedSkillTitles || $[19] !== selectedSkillTitles || $[20] !== setSelectedSkillTitles) {
    t11 = skillItems => {
      const addedTitles = skillItems.map(_temp).filter(title => !selectedSkillTitles.includes(title));
      const removedTitles = addedSkillTitles.filter(title_0 => !skillItems.find(item_0 => item_0.title === title_0));
      setSelectedSkillTitles(x_1 => [...x_1.filter(title_1 => !removedTitles.includes(title_1)), ...addedTitles]);
      setAddedSkillTitles(skillItems.map(_temp2));
    };
    $[18] = addedSkillTitles;
    $[19] = selectedSkillTitles;
    $[20] = setSelectedSkillTitles;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const onSelectedSkillsChange = t11;
  let t12;
  if ($[22] !== selectedSkillTitles || $[23] !== userRoleKeys) {
    t12 = generatePrompt({
      userRoleKeys,
      selectedSkillTitles
    });
    $[22] = selectedSkillTitles;
    $[23] = userRoleKeys;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const generatedPrompt = t12;
  const initialGeneratedPrompt = useRef(generatedPrompt);
  let t13;
  let t14;
  if ($[25] !== generatedPrompt) {
    t13 = () => {
      if (generatedPrompt != initialGeneratedPrompt.current) {
        tracker.memberMatchBuilderModified();
        setIsModified(true);
      } else {
        setIsModified(false);
      }
    };
    t14 = [generatedPrompt];
    $[25] = generatedPrompt;
    $[26] = t13;
    $[27] = t14;
  } else {
    t13 = $[26];
    t14 = $[27];
  }
  useEffect(t13, t14);
  let t15;
  if ($[28] !== draftQuery.data?.scoUserSearchDraft) {
    t15 = () => {
      if (draftQuery.data?.scoUserSearchDraft) {
        setUserSearchDraft(draftQuery.data?.scoUserSearchDraft);
      }
    };
    $[28] = draftQuery.data?.scoUserSearchDraft;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const t16 = draftQuery.data?.scoUserSearchDraft;
  let t17;
  if ($[30] !== t16) {
    t17 = [t16];
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  useEffect(t15, t17);
  let t18;
  if ($[32] !== currentUser?.isRegistered || $[33] !== generatedPrompt || $[34] !== naturalLanguagePrompt || $[35] !== props.userSearch?.userSearchId || $[36] !== router || $[37] !== selectedSkillTitles || $[38] !== upsertUserSearch || $[39] !== userRoleKeys) {
    t18 = async t19 => {
      const isRegistered = t19 === undefined ? false : t19;
      setIsSubmitting(true);
      if (!isRegistered && !currentUser?.isRegistered) {
        setRequireSignup(true);
        setIsSubmitting(false);
        return;
      }
      if (userRoleKeys.length === 0 && selectedSkillTitles.length === 0 && !naturalLanguagePrompt) {
        alert("Select some options to start matching.");
        setIsSubmitting(false);
        return;
      }
      const input = {
        userSearchId: props.userSearch?.userSearchId || null,
        naturalLanguagePrompt: naturalLanguagePrompt || generatedPrompt.replaceAll("*", ""),
        userRoleKeys,
        requiredSkillTitles: selectedSkillTitles,
        reprocess: true
      };
      const result = await upsertUserSearch({
        variables: {
          input
        }
      });
      if (result.error) {
        alert(`Error: ${result.error.toString()}`);
        setIsSubmitting(false);
      }
      tracker.memberMatchBuilderSubmitted(input);
      const userSearch = result.data?.upsertUserSearch?.userSearch;
      if (userSearch) {
        router.push(`/members/matching/${userSearch.userSearchId}`);
      } else {
        alert("Error: userSearchId is empty.");
      }
    };
    $[32] = currentUser?.isRegistered;
    $[33] = generatedPrompt;
    $[34] = naturalLanguagePrompt;
    $[35] = props.userSearch?.userSearchId;
    $[36] = router;
    $[37] = selectedSkillTitles;
    $[38] = upsertUserSearch;
    $[39] = userRoleKeys;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  const onSubmit = t18;
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  let t19;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = seededShuffle(["I need someone to help me set up my marketing analytics", "I'd like to meet other founders in the fintech industry", "I need advice on how to find and pitch investors", "I'm looking for a developer to build my mobile app", "I need a graphic designer to create some social media ads"], hourSeed());
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  const placeholders = t19;
  const placeholder = useDelayedText(placeholders[placeholderIndex]);
  let t20;
  if ($[42] !== naturalLanguagePrompt || $[43] !== placeholderIndex || $[44] !== showNaturalLanguagePrompt) {
    t20 = () => {
      if (naturalLanguagePrompt || !showNaturalLanguagePrompt) {
        return;
      }
      if (placeholderIndex === placeholders.length - 1) {
        setPlaceholderIndex(0);
      } else {
        setPlaceholderIndex(placeholderIndex + 1);
      }
    };
    $[42] = naturalLanguagePrompt;
    $[43] = placeholderIndex;
    $[44] = showNaturalLanguagePrompt;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  useInterval(t20, 4000);
  let t21;
  if ($[46] !== getBreakpointValue) {
    t21 = getBreakpointValue(["md", "md", "lg", "xl", "2xl"]);
    $[46] = getBreakpointValue;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <TrackEvent action="memberMatchBuilderViewed" onlyOnScreen={true} />;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== onSubmit || $[50] !== props.emailCaptureMethod || $[51] !== requireSignup) {
    t23 = requireSignup && props.emailCaptureMethod === "modal" && <UserSearchBuilderSignupModal onDismiss={() => setRequireSignup(false)} onSuccess={() => onSubmit(true)} />;
    $[49] = onSubmit;
    $[50] = props.emailCaptureMethod;
    $[51] = requireSignup;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== getBreakpointValue || $[54] !== showNaturalLanguagePrompt) {
    t24 = showNaturalLanguagePrompt ? 1 : getBreakpointValue([1, 1, 2]);
    $[53] = getBreakpointValue;
    $[54] = showNaturalLanguagePrompt;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== draftQuery.loading || $[57] !== getBreakpointValue || $[58] !== isFormOnRight || $[59] !== onSelectedSkillsChange || $[60] !== primarySkillTitles || $[61] !== props.colors?.labels || $[62] !== props.formBoxProps || $[63] !== props.hide?.additionalSkillTitles || $[64] !== props.hide?.skillSearch || $[65] !== props.hide?.title || $[66] !== props.hide?.userRoleKeys || $[67] !== props.labels?.additionalSkills || $[68] !== props.labels?.primarySkills || $[69] !== props.labels?.title || $[70] !== props.userSearch || $[71] !== selectedSkillTitles || $[72] !== setUserRoleKeys || $[73] !== showNaturalLanguagePrompt || $[74] !== toggleSelectedSkill || $[75] !== userRoleKeys || $[76] !== userSearchDraft.relevantSkills) {
    t25 = !showNaturalLanguagePrompt && <VStack align="stretch" order={isFormOnRight ? 1 : 0}><VStack align="stretch" flexGrow={1} gap={4} padding={8} {...props.formBoxProps}>{!props.hide?.title && <><Badge variant="title">{props.userSearch ? "Edit Member Match" : props.labels?.title || "Start a New Member Match"}</Badge><Separator /></>}{!props.hide?.userRoleKeys && <VStack align="stretch" flexGrow={1} gap={2}><Heading size="sm">I am looking for:</Heading><SimpleGrid columns={getBreakpointValue([2, 2, 4])} gap={1}><SelectableButtons defaultValue={userRoleKeys} maxSelections={1} options={memberMatchUserRolesSelectableButtonOptions} onChange={selections => {
              setUserRoleKeys(selections.map(_temp3));
            }} /></SimpleGrid></VStack>}<VStack align="stretch" flexGrow={1} gap={2}><Heading size="sm" css={{
            "& small": {
              fontWeight: "normal",
              opacity: 0.5
            }
          }}>{props.labels?.primarySkills || <>{"Experience & Skills: "}<chakra.span fontSize="xs" opacity={0.5}>select all that apply</chakra.span></>}</Heading><Wrap gap={2} width="full">{primarySkillTitles.map((skillTitle_0, idx) => <WrapItem key={idx} flex={1}><UserSearchToggleButton key={idx} buttonProps={{
                size: "md"
              }} isSelected={!!selectedSkillTitles.find(s_0 => s_0 === skillTitle_0)} value={skillTitle_0} onToggle={toggleSelectedSkill} /></WrapItem>)}</Wrap>{!props.hide?.skillSearch && <SkillsComboboxInput comboboxTagInputProps={{
            allowMultipleSelections: true,
            placeholder: "Add specific skills..."
          }} onChange={onSelectedSkillsChange} />}</VStack>{!props.hide?.additionalSkillTitles && (userSearchDraft.relevantSkills?.length > 0 || draftQuery.loading) && <Box><HStack align="center" gap={0} justify="space-between" marginBottom="0.5em" padding={0}><Text color={props.colors?.labels || "colorPalette.fg"} textStyle="labelMd">{props.labels?.additionalSkills || <>{"Similar"}{" "}{primarySkillTitles.filter(t => selectedSkillTitles.includes(t)).join(" + ")}{" "}skills:</>}</Text>{draftQuery.loading && <Spinner size="xs" />}</HStack>{userSearchDraft.relevantSkills && <Wrap gap={2} marginBottom={4} marginY={2} opacity={draftQuery.loading ? 0.5 : 1} width="full">{userSearchDraft.relevantSkills.filter(rs => !primarySkillTitles.includes(rs[0])).slice(0, 14).map((t26, idx_0) => {
              const [skillTitle_1] = t26;
              return <WrapItem key={idx_0} flex={1}><UserSearchToggleButton key={idx_0} isSelected={!!selectedSkillTitles.find(s_1 => s_1 === skillTitle_1)} value={skillTitle_1} buttonProps={{
                  padding: "0.5em",
                  fontWeight: "normal",
                  size: "sm"
                }} onToggle={toggleSelectedSkill} /></WrapItem>;
            })}</Wrap>}</Box>}</VStack></VStack>;
    $[56] = draftQuery.loading;
    $[57] = getBreakpointValue;
    $[58] = isFormOnRight;
    $[59] = onSelectedSkillsChange;
    $[60] = primarySkillTitles;
    $[61] = props.colors?.labels;
    $[62] = props.formBoxProps;
    $[63] = props.hide?.additionalSkillTitles;
    $[64] = props.hide?.skillSearch;
    $[65] = props.hide?.title;
    $[66] = props.hide?.userRoleKeys;
    $[67] = props.labels?.additionalSkills;
    $[68] = props.labels?.primarySkills;
    $[69] = props.labels?.title;
    $[70] = props.userSearch;
    $[71] = selectedSkillTitles;
    $[72] = setUserRoleKeys;
    $[73] = showNaturalLanguagePrompt;
    $[74] = toggleSelectedSkill;
    $[75] = userRoleKeys;
    $[76] = userSearchDraft.relevantSkills;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  let t26;
  if ($[78] !== ctaVariant || $[79] !== debouncedMatchedUsersCount || $[80] !== draftQuery.loading || $[81] !== generatedPrompt || $[82] !== isFormOnRight || $[83] !== isMobileVariant || $[84] !== isModified || $[85] !== isSubmitting || $[86] !== naturalLanguagePrompt || $[87] !== onSubmit || $[88] !== placeholder || $[89] !== props.contentBoxProps || $[90] !== props.contentInnerBoxProps || $[91] !== props.ctaBoxProps || $[92] !== props.emailCaptureMethod || $[93] !== props.hide?.matchCount || $[94] !== props.hide?.matchedUsers || $[95] !== props.renderContent || $[96] !== requireSignup || $[97] !== showNaturalLanguagePrompt || $[98] !== upsertUserSearchMutation || $[99] !== userSearchDraft) {
    t26 = props.renderContent && !isModified ? props.renderContent() : <VStack align="stretch" bgColor="bg.darker" height="full" padding={4} {...props.contentBoxProps}><VStack align="stretch" bgColor="colorPalette.bg/50" borderRadius="xl" flexGrow={userSearchDraft.relevantSkills?.length > 0 ? 0 : 1} gap={4} padding={4} {...props.contentInnerBoxProps}>{!isMobileVariant && <VStack bgColor="colorPalette.card" border="1px solid" borderColor="colorPalette.border/25" borderRadius="xl" flexGrow={1} justify="center" padding="2em" {...props.ctaBoxProps}>{showNaturalLanguagePrompt ? <><Heading size="md" width="full">Who would you like to meet?</Heading><AutoResizeTextarea autoFocus={true} fontSize="sm" minHeight={24} placeholder={placeholder} value={naturalLanguagePrompt} onChange={ev => {
              setNaturalLanguagePrompt(ev.target.value);
            }} /><Button opacity={0.5} size="xs" variant="outline" onClick={() => {
              setShowNaturalLanguagePrompt(false);
            }}><LuArrowLeft />Cancel and return to Builder</Button></> : <>{generatedPrompt ? <><MarkdownRenderer typographyStyles={{
                p: {
                  lineHeight: 1.5,
                  fontFamily: "Poppins",
                  fontSize: "1.35em",
                  textAlign: "center"
                }
              }}>{generatedPrompt}</MarkdownRenderer><Button opacity={0.5} size="xs" variant="outline" onClick={() => {
                setNaturalLanguagePrompt(generatedPrompt.replaceAll("*", ""));
                setShowNaturalLanguagePrompt(true);
              }}><LuPencil />Customize</Button></> : <><HStack align="center" justify="center" width="full">{!isFormOnRight && <Icon asChild={true}><LuArrowLeft /></Icon>}<Text textAlign="center" textStyle="md">Start by selecting some options...</Text>{isFormOnRight && <Icon asChild={true}><LuArrowRight /></Icon>}</HStack><Button opacity={0.5} size="xs" variant="outline" onClick={() => {
                setNaturalLanguagePrompt(generatedPrompt.replaceAll("*", ""));
                setShowNaturalLanguagePrompt(true);
              }}><LuPencil />Enter a Custom Search</Button></>}</>}</VStack>}{!showNaturalLanguagePrompt && !props.hide?.matchCount && <Text color="colorPalette.text" fontFamily="Poppins" fontSize="0.9em" fontWeight="bold" paddingTop={0} textAlign="center">{"Good news! You have"}{" "}<chakra.span bgColor="orange.600" border="1px solid" borderColor="orange.border" borderRadius="full" boxShadow="0px 0px 10px 0px {colors.orange.600}" color="colorPalette.contrast" marginX={1} opacity={draftQuery.loading ? 0.5 : 1} paddingX="0.5em" paddingY="0.125em"><SSRGate disableSSR={true}>{userSearchDraft.matchedUsersCount ? <CountUp duration={1} end={userSearchDraft.matchedUsersCount} start={debouncedMatchedUsersCount} easingFn={_temp4} /> : <Spinner as="span" />}</SSRGate></chakra.span>{" "}potential matches.</Text>}{!props.hide?.matchedUsers && !showNaturalLanguagePrompt && !isMobileVariant && userSearchDraft.matchedUsers?.length > 0 && <Wrap gap={2} justify="center" opacity={draftQuery.loading ? 0.5 : 1} paddingX={2}>{userSearchDraft.matchedUsers.map(_temp5)}</Wrap>}{requireSignup && (props.emailCaptureMethod === "inline" || !props.emailCaptureMethod) ? <Presence animationDuration="moderate" present={true} animationName={{
          _open: "slide-from-bottom, fade-in",
          _closed: "slide-to-bottom, fade-out"
        }}><Box paddingTop={2}><Separator marginBottom={6} /><Heading marginY={2} size="lg">First, let's create your free account.</Heading><TrackEvent action="memberMatchEmailCaptureViewed" onlyOnScreen={true} /><UserSignupEmailCaptureForm afterSuccess={async (values, result_0) => {
              tracker.memberMatchEmailCaptured();
              if (result_0?.data?.registerUser?.loginTokenSent) {
                alert("That email address was previously registered. Check your email account for an auto-login link to continue.");
              } else {
                setRequireSignup(false);
                await onSubmit(true);
              }
            }} containerProps={{
              background: "transparent",
              boxShadow: "none",
              padding: 0,
              border: "none"
            }} submitButtonProps={{
              children: ctaVariant.signupButtonLabel(userSearchDraft)
            }} onSocialLoginClick={_temp6} /></Box></Presence> : <Box paddingTop={2}><Button disabled={draftQuery.loading || upsertUserSearchMutation.loading || isSubmitting} loading={draftQuery.loading || upsertUserSearchMutation.loading || isSubmitting} size="xl" variant="cta" width="full" onClick={() => {
            tracker.memberMatchBuilderSubmitClicked();
            onSubmit();
          }}>{ctaVariant.submitButtonLabel(userSearchDraft)}<LuArrowRight /></Button></Box>}</VStack></VStack>;
    $[78] = ctaVariant;
    $[79] = debouncedMatchedUsersCount;
    $[80] = draftQuery.loading;
    $[81] = generatedPrompt;
    $[82] = isFormOnRight;
    $[83] = isMobileVariant;
    $[84] = isModified;
    $[85] = isSubmitting;
    $[86] = naturalLanguagePrompt;
    $[87] = onSubmit;
    $[88] = placeholder;
    $[89] = props.contentBoxProps;
    $[90] = props.contentInnerBoxProps;
    $[91] = props.ctaBoxProps;
    $[92] = props.emailCaptureMethod;
    $[93] = props.hide?.matchCount;
    $[94] = props.hide?.matchedUsers;
    $[95] = props.renderContent;
    $[96] = requireSignup;
    $[97] = showNaturalLanguagePrompt;
    $[98] = upsertUserSearchMutation;
    $[99] = userSearchDraft;
    $[100] = t26;
  } else {
    t26 = $[100];
  }
  let t27;
  if ($[101] !== props.simpleGridProps || $[102] !== t24 || $[103] !== t25 || $[104] !== t26) {
    t27 = <SimpleGrid columns={t24} gap={0} {...props.simpleGridProps}>{t25}{t26}</SimpleGrid>;
    $[101] = props.simpleGridProps;
    $[102] = t24;
    $[103] = t25;
    $[104] = t26;
    $[105] = t27;
  } else {
    t27 = $[105];
  }
  let t28;
  if ($[106] !== props.containerBoxProps || $[107] !== t21 || $[108] !== t23 || $[109] !== t27) {
    t28 = <Box bgColor="colorPalette.container" border="1px solid" borderColor="colorPalette.border" borderRadius={t21} boxShadow="md" overflow="hidden" {...props.containerBoxProps}>{t22}{t23}{t27}</Box>;
    $[106] = props.containerBoxProps;
    $[107] = t21;
    $[108] = t23;
    $[109] = t27;
    $[110] = t28;
  } else {
    t28 = $[110];
  }
  return t28;
};
function _temp(item) {
  return item.title;
}
function _temp2(item_1) {
  return item_1.title;
}
function _temp3(s) {
  return s.key;
}
function _temp4(t_0, b, c, d) {
  return c * ((t_0 = t_0 / d - 1) * t_0 * t_0 + 1) + b;
}
function _temp5(user, idx_1) {
  return <WrapItem key={user.id}><Presence animationDuration="moderate" present={true} style={{
      animationDelay: `${idx_1 / 75}s`
    }} animationName={{
      _open: "fade-in",
      _closed: "fade-out"
    }}><Avatar.Root size="sm"><Avatar.Fallback name={user.fullName} /><Avatar.Image src={user.pictureUrl} /></Avatar.Root></Presence></WrapItem>;
}
function _temp6() {
  tracker.memberMatchSocialLoginClicked();
  return true;
}